import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { ReportsNavComponent } from '../reports-nav.component';
import { PaymentReportService } from '../../../domain/services/payment-report/payment-report.service';
import { PaymentOfficeServiceService } from '../../../domain/services/payment-office/payment-office.service.service';
import {
  PaymentReport, PaymentDetailExport, PaymentBalancingReport, ReportScope, ByCashierEntry,
} from '../../../domain/models/payment-report.model';
import { SummaryTabComponent } from './tabs/summary-tab.component';
import { DetailedTabComponent } from './tabs/detailed-tab.component';
import { BalancingTabComponent } from './tabs/balancing-tab.component';
import { UsersServicesService } from '../../../domain/services/users/users-services.service';
import { InternalUserInfo } from '../../../domain/models/internal-userInfo-model';

interface OfficeOption { id: string; name: string; }

@Component({
  selector: 'app-payment-reports',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatTabsModule, MatProgressBarModule,
    MatDatepickerModule, MatNativeDateModule,
    SummaryTabComponent, DetailedTabComponent, BalancingTabComponent,
    ReportsNavComponent,
  ],
  templateUrl: './payment-reports.component.html',
  styleUrl: './payment-reports.component.css',
})
export class PaymentReportsComponent implements OnInit {
  // Default to today so Run always has a date: an empty Business date made Run do nothing
  // visible on Daily balancing (QA 2026-09-23).
  rangeStart: Date | null = new Date();
  rangeEnd: Date | null = new Date();
  officeId = '';
  cashierUserId = '';
  balancingDate: Date | null = new Date();
  groupBy: 'user' | 'drawer' = 'drawer';
  /** 0 Summary, 1 Detailed, 2 Daily balancing. Decides which inputs and which run the bar drives. */
  activeTab = 0;

  offices: OfficeOption[] = [];
  cashiers: ByCashierEntry[] = [];
  /** Every internal user, loaded once; the User select filters this by office. */
  private allUsers: InternalUserInfo[] = [];

  loading = false;
  error: string | null = null;

  summary: PaymentReport | null = null;
  detail: PaymentDetailExport | null = null;
  balancing: PaymentBalancingReport | null = null;

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly reports: PaymentReportService,
    private readonly officeService: PaymentOfficeServiceService,
    private readonly users: UsersServicesService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'summarize', url: '/app/reports', label: 'Reports' },
      { icon: 'payments', url: '/app/reports/payment-reports', label: 'Payment reports' },
    ]);
    this.officeService.getAll().subscribe({
      next: (list) => {
        this.offices = list.map(o => ({ id: String(o.id), name: o.name }));
      },
      error: () => { /* non-blocking; county-only still works */ },
    });
    this.users.getAll().subscribe({
      next: (list) => { this.allUsers = list; },
      error: () => { /* non-blocking; 'All users' still works */ },
    });
  }

  /**
   * Users selectable as a cashier. Sourced from the internal user list rather than from a
   * report's own paymentsByCashier: that only arrived after a run, so the filter could not
   * be set before the first run and stayed empty whenever the range had no payments.
   */
  get cashierOptions(): { id: string; name: string }[] {
    return this.allUsers
      .filter(u => u.status === 1)
      .filter(u => !this.officeId || String(u.paymentOfficeId) === this.officeId)
      .map(u => ({ id: String(u.id), name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.userName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Format a Date as a local YYYY-MM-DD (avoids UTC day-shift from toISOString). */
  private toIso(d: Date | null): string {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private scope(): ReportScope {
    return {
      dateFrom: this.toIso(this.rangeStart),
      dateTo: this.toIso(this.rangeEnd),
      officeId: this.officeId || undefined,
      cashierUserId: this.cashierUserId || undefined,
    };
  }

  get isBalancing(): boolean { return this.activeTab === 2; }

  /** The single Run button: balancing takes a business date, the other two a date range. */
  run(): void {
    if (this.isBalancing) { this.runBalancing(); } else { this.runSummaryAndDetail(); }
  }

  onOfficeChange(): void {
    this.cashierUserId = '';
  }

  runSummaryAndDetail(): void {
    if (!this.rangeStart || !this.rangeEnd) { this.error = 'Choose a date range.'; return; }
    this.loading = true;
    this.error = null;
    this.reports.getSummary(this.scope()).subscribe({
      next: (r) => {
        this.summary = r;
        this.cashiers = r.paymentsByCashier ?? [];
        this.reports.getDetail(this.scope()).subscribe({
          next: (d) => { this.detail = d; this.loading = false; },
          error: () => { this.error = 'Failed to load detail rows.'; this.loading = false; },
        });
      },
      error: () => { this.error = 'Failed to load summary.'; this.loading = false; },
    });
  }

  runBalancing(): void {
    if (!this.balancingDate) { this.error = 'Choose a business date.'; return; }
    this.loading = true;
    this.error = null;
    this.reports.getBalancing(this.toIso(this.balancingDate), this.scope(), this.groupBy).subscribe({
      next: (b) => { this.balancing = b; this.loading = false; },
      error: () => { this.error = 'Failed to load balancing report.'; this.loading = false; },
    });
  }
}
